export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  orgSlug: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
}

import { resolveTopoloBrowserAuthBaseUrl } from '@topolo-io/app-shell';
import { accessTokenMemory, accessTokenSession, secureAuthClient } from './secure-auth';
import { authClient as topoloAuthClient } from './topolo-auth';

class RoadmapperAuthClient {
  private authUrl: string;

  constructor(authUrl = resolveTopoloBrowserAuthBaseUrl()) {
    this.authUrl = authUrl;
  }

  async checkExistingSession(
    _options: { timeout?: number } = {},
  ): Promise<AuthTokens | null> {
    // SSO disabled: always report no existing session
    return null;
  }

  async login(): Promise<AuthTokens> {
    const url = `${this.authUrl}/login?return_url=${encodeURIComponent(window.location.href)}`;
    window.location.assign(url);
    return new Promise<AuthTokens>(() => {});
  }

  async loginWithOAuth(provider: string): Promise<AuthTokens> {
    const url = `${this.authUrl}/oauth/${provider}/authorize?return_url=${encodeURIComponent(window.location.href)}`;
    window.location.assign(url);
    return new Promise<AuthTokens>(() => {});
  }

  async refreshToken(_refreshToken: string): Promise<AuthTokens> {
    try {
      const result = await secureAuthClient.refreshSession({ allowOnLoginRoute: true });
      const accessToken = result.ok ? result.accessToken : null;
      if (!accessToken) throw new Error('Token refresh failed');
      return { accessToken, refreshToken: '', expiresAt: Date.now() + 3600000 };
    } catch {
      // Silent failure - SSO fallback will handle expired tokens gracefully
      throw new Error('Token refresh failed');
    }
  }

  async logout(): Promise<void> {
    await secureAuthClient.logout();
  }

  /**
   * The shared authenticated fetch. @topolo-io/auth-client owns the whole
   * browser auth contract — bearer attachment, X-App-ID, proactive refresh near
   * expiry, and refresh-and-retry-once on a 401. Building the header from a
   * caller-supplied token pinned a snapshot taken before the request and had no
   * recovery, so the tokens/appId arguments are kept only for call-site
   * compatibility.
   */
  createAuthenticatedFetch(tokens: AuthTokens | null, appId?: string) {
    void tokens;
    void appId;
    return topoloAuthClient.createAuthenticatedFetch();
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }
}

// Token and user storage utilities
export const tokenStorage = {
  getTokens(): AuthTokens | null {
    const accessToken = accessTokenMemory.get() || accessTokenSession.get();
    if (!accessToken) return null;
    return { accessToken, refreshToken: '', expiresAt: Date.now() + 3600000 };
  },

  setTokens(tokens: AuthTokens): void {
    accessTokenMemory.set(tokens.accessToken);
    accessTokenSession.set(tokens.accessToken);
  },

  clearTokens(): void {
    accessTokenMemory.clear();
    accessTokenSession.clear();
  },
};

export const authClient = new RoadmapperAuthClient();
